import mapboxgl from 'mapbox-gl';

import * as turf from '@turf/turf';

import { MapStyle } from 'appConstants';
import {
  ColorGradient,
  format_color_for_mapbox,
} from 'features/chart/colorMaps';
import {
  breakMultiLineToLineFeatures,
  interpAlongLineFeature,
  latLngToJsonPoint,
  lineEndStub,
  lineStartStub,
} from 'features/map/utils/geography_utils';
import {
  addMapLayerInDefinedStack,
  mapHasLayer,
  mapLayerSafeRemove,
  mapSourceSafeRemove,
} from 'features/map/utils/mapCommon';
import {
  getRouteArrowBgLayerDefinition,
  getRouteArrowLayerDefinition,
  mapBgDimmed,
  mapHighContrastColor,
} from 'features/map/utils/mapLayerDefinitions';
import { visibleProperty } from 'features/map/utils/mapUtils';
import {
  MIN_MAP_ZOOM,
  kBarberPolePatternImg,
  kQueueLayerId,
  kQueueMapHoverLayerId,
  kQueuePositionMarkerImg,
  kQueueRouteDimLayerId,
  kQueueRouteSelectionSourceId,
  kQueueSourceId,
  kQueueTagmaLayerId,
  kQueueUnderlayLayerId,
  kRouteArrowsBgLayerId,
  kRouteArrowsLayerId,
} from 'features/map/utils/map_constants';
import {
  kQueueOutlineColor,
  nullCongestionColor,
} from 'features/workflow_queuing/common';
import {
  Feature,
  FeatureCollection,
  LineString,
  MultiLineString,
} from 'geojson';

import { RouteInfo } from '../../appTypes';

const kMapHoverSourceId = 'maphover';

function make_queue_line_width_expression(
  basis_width: number,
): mapboxgl.Expression {
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    MIN_MAP_ZOOM,
    5 * basis_width,
    14,
    5 * basis_width,
    16,
    8 * basis_width,
    19,
    20 * basis_width,
  ];
}

export function updateHoverOffsetOnMap(
  map: mapboxgl.Map,
  mapStyle: MapStyle,
  hoverOffset: null | number,
  feature: RouteInfo,
) {
  if ((!hoverOffset && hoverOffset !== 0) || !feature) {
    mapLayerSafeRemove(map, kQueueMapHoverLayerId);
    mapSourceSafeRemove(map, kMapHoverSourceId);
  } else {
    const mapHoverLayerDefinition: mapboxgl.SymbolLayer = {
      id: kQueueMapHoverLayerId,
      type: 'symbol',
      source: kMapHoverSourceId,
      layout: {
        'icon-image': kQueuePositionMarkerImg,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': 0.6,
      },
      paint: {
        'icon-color': mapHighContrastColor(mapStyle),
        'icon-opacity': 1,
      },
    };
    const interpolated = interpAlongLineFeature(feature, hoverOffset);
    if (interpolated) {
      const bookmarks: FeatureCollection = {
        type: 'FeatureCollection',
        features: [interpolated],
      } as FeatureCollection;
      if (!mapHasLayer(map, kQueueMapHoverLayerId)) {
        map.addSource(kMapHoverSourceId, {
          type: 'geojson',
          data: bookmarks,
        });
        addMapLayerInDefinedStack(map, mapHoverLayerDefinition);
      }
      (map.getSource(kMapHoverSourceId) as any).setData(bookmarks);
    }
  }
}

function getDistOnMap(map: mapboxgl.Map, eventLngLat: mapboxgl.LngLat, point) {
  const mousePx = map.project(eventLngLat);
  const [lng, lat] = point.geometry.coordinates;
  const pointPx = map.project({ lng, lat });
  const dx = mousePx.x - pointPx.x;
  const dy = mousePx.y - pointPx.y;
  const distancePx = Math.hypot(dx, dy);
  return distancePx;
}

export function getHoverOffsetFromMapMouseMove(
  map: mapboxgl.Map,
  event: mapboxgl.MapMouseEvent,
  feature: RouteInfo,
) {
  if (!feature) {
    return { localHoverOffset: undefined, distMeters: undefined };
  }
  const nearestPoint = turf.nearestPointOnLine(
    feature,
    latLngToJsonPoint(event.lngLat),
  );
  const dist = getDistOnMap(map, event.lngLat, nearestPoint);

  let split_length;
  if (feature.geometry.type === 'LineString') {
    const split_start = turf.lineSplit(
      feature as Feature<LineString>,
      nearestPoint,
    ).features[0];
    split_length = turf.length(split_start);
  } else {
    let dist_seen = 0;
    const features = breakMultiLineToLineFeatures(
      feature as Feature<MultiLineString>,
    );
    for (let i = 0; i < features.length; i++) {
      const lsFeature = features[i];
      if (turf.pointToLineDistance(nearestPoint, lsFeature) < 0.001) {
        const split_start = turf.lineSplit(lsFeature, nearestPoint).features[0];
        split_length = dist_seen + turf.length(split_start);
        break;
      } else {
        dist_seen += turf.length(lsFeature);
      }
    }
  }
  return { localHoverOffset: split_length, dist };
}

export function filterForTimestep(timestampStr: null | string) {
  if (!timestampStr) return ['boolean', false];
  return ['==', ['get', 'timestep_at_tz'], timestampStr];
}

export function inTimeFocusState(timestampStr: null | string) {
  return !!timestampStr;
}

export function addQueuesToMap(
  map: mapboxgl.Map,
  mapStyle: MapStyle,
  segment_times,
  tagmas,
  timestampStr: string,
  colormap: ColorGradient,
  route_feature: Feature<MultiLineString | LineString>,
) {
  mapLayerSafeRemove(map, kQueueLayerId);
  mapSourceSafeRemove(map, kQueueSourceId);
  mapLayerSafeRemove(map, kQueueTagmaLayerId);
  mapSourceSafeRemove(map, kQueueTagmaLayerId);
  mapLayerSafeRemove(map, kQueueRouteDimLayerId);

  mapLayerSafeRemove(map, kQueueUnderlayLayerId);
  mapLayerSafeRemove(map, kRouteArrowsLayerId);
  mapLayerSafeRemove(map, kRouteArrowsBgLayerId);
  mapSourceSafeRemove(map, kQueueRouteSelectionSourceId);

  if (segment_times) {
    addMapLayerInDefinedStack(map, {
      id: kQueueRouteDimLayerId,
      type: 'background',
      paint: {
        'background-color': mapBgDimmed(mapStyle, 0.5),
      },
      layout: { visibility: visibleProperty(inTimeFocusState(timestampStr)) },
    });
    map.addSource(kQueueSourceId, {
      type: 'geojson',
      data: segment_times,
    });

    const endOfColorMapColor = colormap.sample(1);

    addMapLayerInDefinedStack(map, {
      id: kQueueLayerId,
      type: 'line',
      source: kQueueLayerId,
      layout: {
        'line-cap': 'round',
      },
      paint: {
        'line-color': [
          'interpolate',
          ['linear'],
          [
            '/',
            [
              'coalesce',
              ['get', 'average_speed_kmph'],
              ['literal', 10000000.0],
            ],
            ['coalesce', ['get', 'freeflow_speed_kmph'], ['literal', 0.01]],
          ],
          ...colormap.toInterleaved(),
          1.01,
          format_color_for_mapbox(endOfColorMapColor),
          1000,
          nullCongestionColor,
        ],
        'line-width': make_queue_line_width_expression(3.0),
      },
      filter: filterForTimestep(timestampStr),
    });
  }
  if (route_feature) {
    const start_point = lineStartStub(route_feature);
    start_point.properties.role = 'start';
    const end_point = lineEndStub(route_feature);
    end_point.properties.role = 'end';
    map.addSource(kQueueRouteSelectionSourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [route_feature, start_point, end_point],
      },
      tolerance: 0,
    });

    addMapLayerInDefinedStack(map, {
      id: kQueueUnderlayLayerId,
      type: 'line',
      source: kQueueRouteSelectionSourceId,
      layout: {
        'line-cap': 'butt',
      },
      paint: {
        'line-color': mapHighContrastColor(mapStyle),
        'line-width': make_queue_line_width_expression(4.25),
      },
      filter: inTimeFocusState(timestampStr) as any,
    });

    addMapLayerInDefinedStack(map, {
      ...getRouteArrowLayerDefinition(mapStyle),
      source: kQueueRouteSelectionSourceId,
    });

    addMapLayerInDefinedStack(map, {
      ...getRouteArrowBgLayerDefinition(mapStyle),
      source: kQueueRouteSelectionSourceId,
    });
  }

  if (tagmas) {
    map.addSource(kQueueTagmaLayerId, {
      type: 'geojson',
      data: tagmas,
    });

    addMapLayerInDefinedStack(map, {
      id: kQueueTagmaLayerId,
      type: 'line',
      source: kQueueTagmaLayerId,
      layout: {
        'line-cap': 'butt',
      },
      paint: {
        'line-color': kQueueOutlineColor,
        'line-pattern': kBarberPolePatternImg,
        'line-width': make_queue_line_width_expression(3),
      },
      filter: filterForTimestep(timestampStr),
    });
  }
}

export function setMapQueuesState(
  map: mapboxgl.Map,
  timestampStr: string,
  queue_id: number,
) {
  if (map) {
    if (mapHasLayer(map, kQueueLayerId)) {
      map.setFilter(kQueueLayerId, filterForTimestep(timestampStr));
    }
    if (mapHasLayer(map, kQueueTagmaLayerId)) {
      map.setFilter(kQueueTagmaLayerId, filterForTimestep(timestampStr));
    }

    if (mapHasLayer(map, kQueueUnderlayLayerId)) {
      map.setFilter(kQueueUnderlayLayerId, inTimeFocusState(timestampStr));
    }
    if (mapHasLayer(map, kQueueRouteDimLayerId)) {
      map.setLayoutProperty(
        kQueueRouteDimLayerId,
        'visibility',
        visibleProperty(inTimeFocusState(timestampStr)),
      );
    }
  }
}
